import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton
} from '@mui/material';
import {
  DocumentScanner as DocumentScannerIcon,
  Chat as ChatIcon,
  Psychology as PsychologyIcon,
  Paid as PaidIcon
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';

/** Устойчивое чтение полей totals (имена могут отличаться на бэкенде) */
const readTotals = (totals) => {
  const t = totals || {};
  return {
    count: t.count ?? t.totalCount ?? t.total ?? 0,
    testCount: t.testCount ?? t.test_count ?? 0,
    avgTime: t.avgTime ?? t.avgProcessingTime ?? t.avg_time ?? null,
    minTime: t.minTime ?? t.minProcessingTime ?? null,
    maxTime: t.maxTime ?? t.maxProcessingTime ?? null,
    avgTotalTokens: t.avgTotalTokens ?? t.avgTokens ?? null,
    avgInputTokens: t.avgInputTokens ?? null,
    avgOutputTokens: t.avgOutputTokens ?? null,
    totalCostUsd: t.totalCostUsd ?? t.totalCost ?? t.sumCostUsd ?? 0
  };
};

/** Устойчивое чтение полей daily-записей */
const readDaily = (daily) =>
  (daily || []).map((d) => ({
    date: d.date,
    count: d.count ?? 0,
    avgTime: d.avgTime ?? d.avgProcessingTime ?? null,
    avgTokens: d.avgTokens ?? d.avgTotalTokens ?? null,
    sumCost: d.sumCost ?? d.totalCostUsd ?? 0
  }));

const formatSec = (ms) => {
  if (ms == null) return '—';
  return `${(ms / 1000).toFixed(1)} с`;
};

const formatDateLabel = (date) => {
  if (!date) return '';
  const parts = String(date).split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
  return String(date);
};

const StatTile = ({ icon, value, label, subvalue }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Box>
          <Typography variant="h5" fontWeight="bold">{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {subvalue && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subvalue}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Статистика нейросети: тайлы + графики по дням.
 * Выбор периода находится в заголовке страницы (NeuroPage), как на дашборде.
 */
const NeuroStats = ({ stats, loading }) => {
  const scans = readTotals(stats?.scans?.totals);
  const chat = readTotals(stats?.chat?.totals);

  const scansDaily = readDaily(stats?.scans?.daily);
  const chatDaily = readDaily(stats?.chat?.daily);

  const avgTokensScan = scans.avgTotalTokens ??
    ((scans.avgInputTokens ?? 0) + (scans.avgOutputTokens ?? 0) || null);
  const avgTokensChat = chat.avgTotalTokens ??
    ((chat.avgInputTokens ?? 0) + (chat.avgOutputTokens ?? 0) || null);
  const avgTokensCombined =
    avgTokensScan != null && avgTokensChat != null
      ? ((avgTokensScan * scans.count + avgTokensChat * chat.count) / ((scans.count + chat.count) || 1))
      : (avgTokensScan ?? avgTokensChat);

  const totalCost = (scans.totalCostUsd || 0) + (chat.totalCostUsd || 0);

  // Объединяем дни из обеих серий
  const dateSet = new Set([...scansDaily, ...chatDaily].map((d) => d.date).filter(Boolean));
  const dates = Array.from(dateSet).sort();
  const scanByDate = new Map(scansDaily.map((d) => [d.date, d]));
  const chatByDate = new Map(chatDaily.map((d) => [d.date, d]));

  const chartLabels = dates.map(formatDateLabel);
  const scanTimeSeries = dates.map((d) => {
    const v = scanByDate.get(d)?.avgTime;
    return v != null ? +(v / 1000).toFixed(2) : null;
  });
  const chatTimeSeries = dates.map((d) => {
    const v = chatByDate.get(d)?.avgTime;
    return v != null ? +(v / 1000).toFixed(2) : null;
  });
  const scanTokensSeries = dates.map((d) => scanByDate.get(d)?.avgTokens ?? null);
  const chatTokensSeries = dates.map((d) => chatByDate.get(d)?.avgTokens ?? null);

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<DocumentScannerIcon color="primary" />}
            value={formatSec(scans.avgTime)}
            label="Среднее время сканирования"
            subvalue={`всего сканов: ${scans.count} (тестовых: ${scans.testCount})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<ChatIcon color="info" />}
            value={formatSec(chat.avgTime)}
            label="Среднее время отклика в чате"
            subvalue={`сообщений: ${chat.count}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<PsychologyIcon color="warning" />}
            value={avgTokensCombined != null ? Math.round(avgTokensCombined).toLocaleString('ru-RU') : '—'}
            label="Среднее число токенов"
            subvalue={
              scans.minTime != null && scans.maxTime != null
                ? `min–max время: ${formatSec(scans.minTime)} – ${formatSec(scans.maxTime)}`
                : null
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<PaidIcon color="success" />}
            value={`$${totalCost.toFixed(4)}`}
            label="Расход за период"
            subvalue={`сканы $${(scans.totalCostUsd || 0).toFixed(4)} + чат $${(chat.totalCostUsd || 0).toFixed(4)}`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Время ответа по дням</Typography>
              {dates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }} align="center">
                  Нет данных за период
                </Typography>
              ) : (
                <LineChart
                  height={260}
                  xAxis={[{ data: chartLabels, scaleType: 'band' }]}
                  series={[
                    { data: scanTimeSeries, label: 'Сканы, с', showMark: false },
                    { data: chatTimeSeries, label: 'Чат, с', showMark: false }
                  ]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Токены по дням</Typography>
              {dates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }} align="center">
                  Нет данных за период
                </Typography>
              ) : (
                <LineChart
                  height={260}
                  xAxis={[{ data: chartLabels, scaleType: 'band' }]}
                  series={[
                    { data: scanTokensSeries, label: 'Сканы', showMark: false },
                    { data: chatTokensSeries, label: 'Чат', showMark: false }
                  ]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NeuroStats;
